import { Theme, css, useTheme } from '@emotion/react';

import { useRouter } from 'next/router';

import { ReactNode, SyntheticEvent, useCallback } from 'react';

import { FontSizes, Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Crown, Icon } from 'chatie-design-system/icon';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { CSSProps, OnClickProps } from '@utils/misc';
import { useWebView } from '@utils/webview';

type ListItemProps = {
  rank?: ReactNode;
  left?: ReactNode;
  right?: ReactNode;
  href?: string;
};

const listItemCSS = (isClickable: boolean) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: inherit;
  text-decoration: none;
  ${isClickable &&
  css`
    cursor: pointer;
  `}
`;

const rankSectionCSS = css`
  width: 28px;
  text-align: center;
  margin-right: ${Spacing.medium}px;
`;

const leftSectionCSS = (theme: Theme) => css`
  flex-grow: 1;
  min-width: 0;

  h6 + small,
  small + small {
    display: block;
    ${Typography.body12};
    margin-top: ${Spacing.xsmall}px;
    color: ${theme.var.gray500};
  }

  small + small {
    margin-top: ${Spacing.xxsmall}px;
  }
`;

const rightSectionCSS = css`
  flex-shrink: 0;
`;

const ListItem = ({
  className,
  rank,
  left,
  right,
  href,
  onClick,
}: ListItemProps & OnClickProps & CSSProps) => {
  const content = (
    <>
      {rank && <div css={rankSectionCSS}>{rank}</div>}
      <div css={leftSectionCSS}>{left}</div>
      <div css={rightSectionCSS}>{right}</div>
    </>
  );

  if (href) {
    return (
      <a
        css={listItemCSS(true)}
        className={className}
        href={href}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <div css={listItemCSS(!!onClick)} className={className} onClick={onClick}>
      {content}
    </div>
  );
};

export default ListItem;

type LinkItemProps = {
  to?: string;
  hideArrow?: boolean;
  newTab?: boolean;
  legacy?: boolean;
  onClick?: () => void;
} & ListItemProps;

const linkItemRightCSS = css`
  display: flex;
  align-items: center;
  ${Typography.body12}
`;

export const LinkItem = ({
  to,
  hideArrow = false,
  newTab,
  legacy,
  rank,
  left,
  right,
  className,
  onClick,
}: LinkItemProps & CSSProps) => {
  const theme = useTheme();

  const { web, openLegacyPageSafe } = useWebView();
  const { platform } = useGlobalContext();
  const router = useRouter();

  const handleClick = useCallback(
    (event: SyntheticEvent<Element>) => {
      event.preventDefault();

      if (!to) {
        return;
      }

      onClick?.();

      if (platform !== Platform.web) {
        if (newTab) {
          web.openPage(to);
        } else if (legacy) {
          openLegacyPageSafe(to);
        } else {
          router.push(to);
        }
      } else {
        router.push(to);
      }
    },
    [legacy, newTab, onClick, openLegacyPageSafe, platform, router, to, web]
  );

  return (
    <ListItem
      rank={rank}
      left={left}
      right={
        (to || onClick) && !hideArrow ? (
          <div css={linkItemRightCSS}>
            {right}
            <Icon
              icon={ArrowRight}
              size={Sizes.small}
              color={theme.var.gray500}
            />
          </div>
        ) : (
          right
        )
      }
      href={to}
      onClick={to ? handleClick : onClick}
      className={className}
    />
  );
};

type CrownRankProps = {
  rank: number;
};

const crownRankCSS = css`
  margin-right: ${Spacing.medium}px;
  width: 32px;
  font-size: ${FontSizes.medium}px;
  text-align: center;
`;

export const CrownRank = ({ rank }: CrownRankProps) => {
  const theme = useTheme();
  return (
    <div css={crownRankCSS} data-test="Crown">
      {rank <= 3 ? (
        <Icon
          icon={Crown}
          color={
            rank === 1
              ? theme.colors.yellow400
              : rank === 2
              ? '#c3ced1'
              : '#e5926f'
          }
          size={Sizes.large}
        />
      ) : (
        <h5>{rank}</h5>
      )}
    </div>
  );
};
